import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject } from 'rxjs'
import { environment } from 'src/environments/environment'

export interface CheckoutField {
  key: string
  label: string
  show: 'on' | 'off'
  required: 'on' | 'off'
  type?: 'text' | 'email' | 'number' | 'tel'
}

interface CheckoutFieldsResponse {
  source: string
  fields: CheckoutField[]
}

@Injectable({
  providedIn: 'root'
})
export class CheckoutFieldsService {
  private readonly fieldsSubject = new BehaviorSubject<CheckoutField[]>([])
  readonly fields$ = this.fieldsSubject.asObservable()

  private readonly sourceSubject = new BehaviorSubject<string>('manual')
  readonly source$ = this.sourceSubject.asObservable()

  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false)
  readonly isLoading$ = this.isLoadingSubject.asObservable()

  private readonly errorSubject = new BehaviorSubject<string | null>(null)
  readonly error$ = this.errorSubject.asObservable()

  private readonly agentsUrl = environment.agentsUrl

  constructor(private readonly http: HttpClient) {}

  /**
   * Loads the checkout fields for the project's enabled shipping provider from stacks-agents
   * and publishes them through fields$ and source$. When no provider is enabled the backend
   * returns the default field set with source='manual', so the client never needs its own fallback list.
   * @param projectId - The merchant's project ID.
   */
  async loadFields(projectId: string): Promise<void> {
    this.isLoadingSubject.next(true)
    this.errorSubject.next(null)
    try {
      const url = `${this.agentsUrl}shipping/checkout-fields`
      const response = await this.http
        .post<CheckoutFieldsResponse>(url, { projectId })
        .toPromise()
      this.fieldsSubject.next(response?.fields ?? [])
      this.sourceSubject.next(response?.source ?? 'manual')
    } catch {
      this.errorSubject.next('Failed to load checkout fields')
    } finally {
      this.isLoadingSubject.next(false)
    }
  }

  /**
   * Returns the current cached checkout fields without triggering a reload.
   * @returns The latest published field list.
   */
  getFields(): CheckoutField[] {
    return this.fieldsSubject.getValue()
  }

  /**
   * Returns the current source of checkout fields.
   * @returns The source: 'manual' (editable) or provider name like 'aymakan' (read-only).
   */
  getSource(): string {
    return this.sourceSubject.getValue()
  }

  /**
   * Determines if checkout fields are editable based on their source.
   * Fields are editable only when source is 'manual'.
   * @returns True if fields can be edited, false if they are provider-locked.
   */
  areFieldsEditable(): boolean {
    return this.getSource() === 'manual'
  }
}
